/**
 * Structured Data (JSON-LD) utilities for SEO
 *
 * These functions generate JSON-LD structured data that helps search engines
 * understand the content and improve SEO rankings.
 */

import { GEO_PAGE_DESCRIPTIONS, GEO_SITE_TAGLINE } from '../static/geoPageInfo';

const HOTMOB_PRODUCTION_DOMAIN = process.env.REACT_APP_BASE_URL;

/**
 * Generate Organization structured data
 * Based on schema.org Organization with full structure for GEO.
 * Hotmob entity data is fixed — call with no args from pages.
 */
export const generateOrganizationSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization`,
    name: 'Hotmob',
    legalName: 'Hotmob Limited',
    alternateName: ['hot-mob'],
    url: `${HOTMOB_PRODUCTION_DOMAIN}`,
    logo: {
        '@type': 'ImageObject',
        '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#logo`,
        url: `${HOTMOB_PRODUCTION_DOMAIN}/img/static/hotmob_logo_v1.png`,
        width: 1336,
        height: 996
    },
    description: GEO_PAGE_DESCRIPTIONS.HOME,
    foundingDate: '2007-05-22',
    address: {
        '@type': 'PostalAddress',
        streetAddress: '20/F., Harbourside HQ, 8 Lam Chak Street',
        addressLocality: 'Kowloon Bay',
        addressRegion: 'Kowloon',
        addressCountry: 'Hong Kong'
    },
    contactPoint: [
        {
            '@type': 'ContactPoint',
            telephone: '+852 3728 2400',
            email: 'info@hot-mob.com',
            contactType: 'customer service',
            url: `${HOTMOB_PRODUCTION_DOMAIN}/#contact-us`
        },
        {
            '@type': 'ContactPoint',
            telephone: '+852 3728 2405',
            email: 'info@hot-mob.com',
            contactType: 'customer service',
            url: `${HOTMOB_PRODUCTION_DOMAIN}/#contact-us`
        }
    ],
    sameAs: [
        'https://www.facebook.com/HotmobMobile',
        'https://www.linkedin.com/company/hotmob/',
        'https://showcase.hot-mob.com/',
        'https://www.instagram.com/hotmob.hk/'
    ],
    hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: 'Hotmob Services',
        itemListElement: [
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    '@id': `${HOTMOB_PRODUCTION_DOMAIN}/media-service/#service`,
                    name: 'Media Service',
                    url: `${HOTMOB_PRODUCTION_DOMAIN}/media-service`,
                    description: GEO_PAGE_DESCRIPTIONS.MEDIA_SERVICE
                }
            },
            {
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    '@id': `${HOTMOB_PRODUCTION_DOMAIN}/data-service/#service`,
                    name: 'Data Service',
                    url: `${HOTMOB_PRODUCTION_DOMAIN}/data-service`,
                    description: GEO_PAGE_DESCRIPTIONS.DATA_SERVICE
                }
            }
        ]
    },
    knowsAbout: [
        'Digital Marketing',
        'Digital Advertising',
        'Programmatic Advertising',
        'Data Analytics',
        'China Marketing',
        'Big Data',
        'Search Engine Marketing',
        'Ad Serving',
        'Media Planning',
        'First-party Data',
        'Premium ad placement',
        'AI Content Creation',
        'Data enrichment',
        'External data',
        'Consumer insights',
        'Market intelligence',
        'ETL data integration',
        'Data warehousing',
        'AI and ML analytics'
    ]
});

/**
 * Generate WebPage structured data
 */
export const generateWebPageSchema = (data = {}) => {
    const fullUrl = data.path ? `${HOTMOB_PRODUCTION_DOMAIN}${data.path}` : `${HOTMOB_PRODUCTION_DOMAIN}`;

    return {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${fullUrl}/#webpage`,
        url: fullUrl,
        ...(data.path && {
            breadcrumb: { '@id': `${fullUrl}/#breadcrumb` }
        }),
        name: data.title,
        description: data.description,
        inLanguage: 'en-US',
        isPartOf: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#website` },
        publisher: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` },
        about: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#${data.about}` },
        ...(data.mainEntity && {
            mainEntity: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}${data.mainEntity}` }
        }),
        ...(data.image && {
            primaryImageOfPage: {
                '@type': 'ImageObject',
                '@id': `${fullUrl}/#primaryimage`,
                url: `${HOTMOB_PRODUCTION_DOMAIN}${data.image.path}`,
                ...(data.image.width && { width: data.image.width }),
                ...(data.image.height && { height: data.image.height })
            }
        })
    };
};

/**
 * Generate Service structured data (standalone JSON-LD document)
 */
export const generateServiceSchema = (data = {}) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${HOTMOB_PRODUCTION_DOMAIN}${data.path}/#service`,
    name: data.name,
    // TODO: Add serviceType, based on organization schema knowsAbout
    ...(data.serviceType && {
        serviceType: data.serviceType
    }),
    description: data.description,
    url: `${HOTMOB_PRODUCTION_DOMAIN}${data.path}`,
    provider: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` },
    areaServed: [
        {
            '@type': 'Country',
            name: 'Hong Kong'
        }
    ],
    ...(data.catalogList && {
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: data.name,
            itemListElement: data.catalogList.map((catalog) => ({
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    '@id': `${HOTMOB_PRODUCTION_DOMAIN}${catalog.url}/#service`,
                    name: catalog.name,
                    description: catalog.description,
                    url: `${HOTMOB_PRODUCTION_DOMAIN}${catalog.url}`
                }
            }))
        }
    }),
    ...(data.category && {
        category: data.category,
        isRelatedTo: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/${data.category.toLowerCase().replace(/ /g, '-')}/#service` }
    })
});

/**
 * Generate BreadcrumbList structured data
 */
export const generateBreadcrumbSchema = (data) => {
    const baseUrl = process.env.REACT_APP_BASE_URL;

    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        '@id': `${HOTMOB_PRODUCTION_DOMAIN}${data.pagePath}/#breadcrumb`,
        itemListElement: data.items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url ? `${HOTMOB_PRODUCTION_DOMAIN}${item.url}` : HOTMOB_PRODUCTION_DOMAIN
        }))
    };
};

/**
 * Generate Article structured data (for blog posts)
 * GEO: mainEntityOfPage and speakable help AI/voice systems cite and quote content.
 */
export const generateBlogPostingSchema = (data = {}) => {
    const baseUrl = data.baseUrl || process.env.REACT_APP_BASE_URL;
    const fullUrl = `${baseUrl}${data.path || ''}`;

    const authorName = data.author || 'Hotmob';

    // GEO: schema.org expects speakable as SpeakableSpecification (cssSelector) or URL — not a bare string[].
    let speakableNode;
    if (data.speakable) {
        if (Array.isArray(data.speakable) && data.speakable.length > 0 && typeof data.speakable[0] === 'string') {
            speakableNode = {
                '@type': 'SpeakableSpecification',
                cssSelector: data.speakable
            };
        } else if (typeof data.speakable === 'object' && !Array.isArray(data.speakable)) {
            speakableNode = data.speakable;
        }
    }

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        '@id': `${HOTMOB_PRODUCTION_DOMAIN}${data.path}/#article`,
        url: `${HOTMOB_PRODUCTION_DOMAIN}${data.path}`,
        isPartOf: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/${data.path}/#webpage` },
        headline: data.title,
        description: data.description,
        ...(data.abstract && { abstract: data.abstract }),
        datePublished: data.datePublished,
        dateModified: data.dateModified || data.datePublished,
        articleSection: 'Blog',
        inLanguage: 'en-US',
        ...(data.image && {
            image: Array.isArray(data.image) ? data.image : [data.image]
        }),
        author: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` },
        publisher: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` },
        mainEntityOfPage: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}${data.path}/#webpage` },
        ...(speakableNode && { speakable: speakableNode })
    };

    return schema;
};

/**
 * Generate WebSite structured data (GEO: site-level context for AI engines)
 * Use once per site (e.g. on home or layout). Enables sitelinks/search box in results.
 */
export const generateWebSiteSchema = () => ({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#website`,
    name: 'Hotmob',
    alternateName: ['Hotmob Limited', 'hot-mob'],
    url: `${HOTMOB_PRODUCTION_DOMAIN}`,
    description: GEO_PAGE_DESCRIPTIONS.HOME,
    inLanguage: 'en-US',
    copyrightYear: new Date().getFullYear(),
    copyrightHolder: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` },
    publisher: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` },
    about: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` }
});

/**
 * Generate FAQPage structured data (GEO: Q&A format is highly cited by AI answer engines)
 * Use on static service pages or blog posts that contain clear Q&A content.
 */
export const generateFAQSchema = (data = {}) => {
    const baseUrl = data.baseUrl || process.env.REACT_APP_BASE_URL;
    const fullUrl = `${baseUrl}${data.path || ''}`;

    if (!data.faqs || !Array.isArray(data.faqs) || data.faqs.length === 0) {
        return null;
    }

    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: data.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            }
        })),
        ...(fullUrl && { url: fullUrl })
    };
};

/**
 * Combine schema nodes into one JSON-LD document with @graph.
 * Strips per-node @context so the graph owns a single shared context.
 */
export const buildSchemaGraph = (nodes = []) => {
    const graph = (nodes || []).filter(Boolean).map(({ '@context': _context, ...node }) => node);

    if (graph.length === 0) return null;

    return {
        '@context': 'https://schema.org',
        '@graph': graph
    };
};

export const generateCollectionPageSchema = (data = {}) => {
    const fullUrl = data.path ? `${HOTMOB_PRODUCTION_DOMAIN}${data.path}` : `${HOTMOB_PRODUCTION_DOMAIN}`;
    const items = Array.isArray(data.items) ? data.items : [];

    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${fullUrl}/#collectionpage`,
        url: fullUrl,
        ...(data.path && {
            breadcrumb: { '@id': `${fullUrl}/#breadcrumb` }
        }),
        name: data.name,
        description: data.description,
        inLanguage: 'en-US',
        isPartOf: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#website` },
        publisher: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#organization` },
        ...(data.about && {
            about: { '@id': `${HOTMOB_PRODUCTION_DOMAIN}/#${data.about}` }
        }),
        mainEntity: {
            '@type': 'ItemList',
            numberOfItems: items.length,
            itemListElement: items.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
                url: `${HOTMOB_PRODUCTION_DOMAIN}${item.url}`
            }))
        }
    };
};

/**
 * Render JSON-LD script tag
 */
export const renderStructuredData = (schema) => {
    if (!schema) return null;

    return <script type="application/ld+json">{JSON.stringify(schema).replace(/<\/script>/gi, '<\\/script>')}</script>;
};

/**
 * Render multiple structured data schemas (skips null/undefined entries, e.g. optional FAQ)
 * Returns an array of script elements so Helmet receives direct children (Fragment can prevent SSR from collecting them).
 */
export const renderMultipleStructuredData = (schemas = []) => {
    const valid = (schemas || []).filter(Boolean);
    if (valid.length === 0) return null;

    return valid.map((schema, index) => (
        <script key={index} type="application/ld+json">
            {JSON.stringify(schema).replace(/<\/script>/gi, '<\\/script>')}
        </script>
    ));
};
